import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import { Analytics, SystemSettings, AuditLog } from '../../types';
import {
  Users,
  Activity,
  Settings,
  BarChart2,
  Server,
  AlertCircle,
  UserPlus,
  UserMinus,
  Clock,
  Shield
} from 'lucide-react';

export const AdminDashboard: React.FC = () => {
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [recentLogs, setRecentLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [analyticsData, settingsData, logsData] = await Promise.all([
          adminAPI.getAnalytics(),
          adminAPI.getSystemSettings(),
          adminAPI.getAuditLogs(1, 5)
        ]);
        setAnalytics(analyticsData);
        setSettings(settingsData);
        setRecentLogs(logsData?.logs || []);
      } catch (err: any) {
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner h-8 w-8"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500 flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-white mb-8">Admin Dashboard</h1>

        {/* System Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">System Status</h3>
              <Server className="w-5 h-5 text-blue-400" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Maintenance Mode</span>
                <span className={`px-2 py-1 rounded-full text-sm ${settings?.maintenance_mode ? 'bg-red-900/20 text-red-400' : 'bg-green-900/20 text-green-400'}`}>
                  {settings?.maintenance_mode ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Registration</span>
                <span className={`px-2 py-1 rounded-full text-sm ${settings?.registration_enabled ? 'bg-green-900/20 text-green-400' : 'bg-red-900/20 text-red-400'}`}>
                  {settings?.registration_enabled ? 'Open' : 'Closed'}
                </span>
              </div>
            </div>
          </div>

          {/* User Stats */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">User Statistics</h3>
              <Users className="w-5 h-5 text-blue-400" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Total Users</span>
                <span className="text-white">{analytics?.users?.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Active Users</span>
                <span className="text-white">{analytics?.users?.active || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">New Today</span>
                <span className="text-white">{analytics?.users?.new_today || 0}</span>
              </div>
            </div>
          </div>

          {/* Challenge Stats */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Challenge Statistics</h3>
              <Activity className="w-5 h-5 text-blue-400" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Total Challenges</span>
                <span className="text-white">{analytics?.challenges?.total || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Active Challenges</span>
                <span className="text-white">{analytics?.challenges?.active || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Completion Rate</span>
                <span className="text-white">{analytics?.challenges?.completion_rate || 0}%</span>
              </div>
            </div>
          </div>

          {/* System Metrics */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">System Metrics</h3>
              <BarChart2 className="w-5 h-5 text-blue-400" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">CPU Usage</span>
                <span className="text-white">{analytics?.system?.cpu_usage || 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Memory Usage</span>
                <span className="text-white">{analytics?.system?.memory_usage || 0}%</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Active Containers</span>
                <span className="text-white">{analytics?.system?.active_containers || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white">Recent Activity</h3>
            <Clock className="w-5 h-5 text-blue-400" />
          </div>
          <div className="space-y-4">
            {recentLogs && recentLogs.length > 0 ? (
              recentLogs.map((log) => (
                <div key={log.id} className="flex items-start space-x-4 p-4 bg-gray-700/50 rounded-lg">
                  <div className="flex-shrink-0">
                    {log.action.includes('create') && <UserPlus className="w-5 h-5 text-green-400" />}
                    {log.action.includes('delete') && <UserMinus className="w-5 h-5 text-red-400" />}
                    {log.action.includes('update') && <Settings className="w-5 h-5 text-blue-400" />}
                    {log.action.includes('login') && <Shield className="w-5 h-5 text-yellow-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white">{log.details}</p>
                    <div className="mt-1 flex items-center text-xs text-gray-400">
                      <span>{new Date(log.created_at).toLocaleString()}</span>
                      <span className="mx-2">•</span>
                      <span>{log.ip_address}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-400 py-4">
                No recent activity to display
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}; 